import { useEffect, useState } from "react";

function ArtistBookingModal({
    isOpen,
    onClose,
    onSaved,
    artistId,
    selectedDates,
    booking
}) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [status, setStatus] = useState("confirmed");

    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState("");

    /*
     * Edit mode
     */
    const isEditMode = Boolean(booking?.id);

    /*
     * Populate form when modal opens
     */
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setError("");

        /*
         * EDIT MODE
         */
        if (booking) {
            setTitle(booking.title || "");

            setDescription(
                booking.description || ""
            );

            setStartDate(
                formatDateTimeLocal(
                    booking.startDate
                )
            );

            setEndDate(
                formatDateTimeLocal(
                    booking.endDate
                )
            );

            setStatus(
                booking.status || "confirmed"
            );

            return;
        }

        /*
         * ADD MODE
         */
        setTitle("");
        setDescription("");
        setStatus("confirmed");

        if (selectedDates) {
            setStartDate(
                formatDateTimeLocal(
                    selectedDates.start
                )
            );

            setEndDate(
                formatDateTimeLocal(
                    selectedDates.end
                )
            );
        } else {
            setStartDate("");
            setEndDate("");
        }
    }, [isOpen, booking, selectedDates]);

    /*
     * Convert Date / ISO string
     *
     * YYYY-MM-DDTHH:mm
     */
    const formatDateTimeLocal = (value) => {
        if (!value) {
            return "";
        }

        const date = new Date(value);

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return "";
        }

        const year =
            date.getFullYear();

        const month =
            String(
                date.getMonth() + 1
            ).padStart(2, "0");

        const day =
            String(
                date.getDate()
            ).padStart(2, "0");

        const hours =
            String(
                date.getHours()
            ).padStart(2, "0");

        const minutes =
            String(
                date.getMinutes()
            ).padStart(2, "0");

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    /*
     * Validate form
     */
    const validateForm = () => {
        if (!title.trim()) {
            setError(
                "Please enter booking title."
            );

            return false;
        }

        if (!startDate) {
            setError(
                "Please select start date."
            );

            return false;
        }

        if (!endDate) {
            setError(
                "Please select end date."
            );

            return false;
        }

        const start =
            new Date(startDate);

        const end =
            new Date(endDate);

        if (
            Number.isNaN(
                start.getTime()
            )
        ) {
            setError(
                "Invalid start date."
            );

            return false;
        }

        if (
            Number.isNaN(
                end.getTime()
            )
        ) {
            setError(
                "Invalid end date."
            );

            return false;
        }

        if (end <= start) {
            setError(
                "End date/time must be after start date/time."
            );

            return false;
        }

        return true;
    };

    /*
     * ADD / EDIT booking
     */
    const handleSubmit = async (event) => {
        event.preventDefault();

        setError("");

        if (!artistId) {
            setError(
                "Artist ID is missing."
            );

            return;
        }

        if (!validateForm()) {
            return;
        }

        try {
            setSaving(true);

            const token =
                sessionStorage.getItem(
                    "JWT_TOKEN"
                );

            const start =
                new Date(startDate);

            const end =
                new Date(endDate);

            let url = "";
            let method = "";

            /*
             * EDIT
             */
            if (isEditMode) {
                url = `${process.env.REACT_APP_API_URL}/editartistbooking/${booking.id}`;
                method = "PUT";
            }

            /*
             * ADD
             */
            else {
                url = `${process.env.REACT_APP_API_URL}/addartistbooking`;
                method = "POST";
            }

            const requestBody = {
                title: title.trim(),
                description: description.trim(),
                startDate: start.toISOString(),
                endDate: end.toISOString(),
                status
            };

            /*
             * artistId is required only for ADD
             *
             * EDIT API does not need artistId.
             */
            if (!isEditMode) {
                requestBody.artistId = artistId;
            }

            const response =
                await fetch(
                    url,
                    {
                        method,
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": "Bearer " + token
                        },
                        body:
                            JSON.stringify(
                                requestBody
                            )
                    }
                );

            const data = await response.json();
            console.log("ResponseData: ", response);
            if (!response.ok) {
                throw new Error(
                    data?.message ||
                    (
                        isEditMode
                            ? "Failed to update booking."
                            : "Failed to create booking."
                    )
                );
            }

            /*
             * Refresh calendar
             */
            if (onSaved) {
                await onSaved(data);
            }

            /*
             * Close modal
             */
            onClose();

        } catch (error) {
            console.error(
                isEditMode
                    ? "Failed to update booking:"
                    : "Failed to create booking:",
                error
            );

            setError(
                error.message ||
                (
                    isEditMode
                        ? "Failed to update booking."
                        : "Failed to create booking."
                )
            );

        } finally {
            setSaving(false);
        }
    };

    /*
     * DELETE booking
     */
    const handleDelete = async () => {
        if (!booking?.id) {
            setError(
                "Booking ID is missing."
            );

            return;
        }

        const confirmed =
            window.confirm(
                `Are you sure you want to delete "${booking.title}"?`
            );

        if (!confirmed) {
            return;
        }

        try {
            setDeleting(true);
            setError("");

            const token =
                sessionStorage.getItem(
                    "JWT_TOKEN"
                );

            const response =
                await fetch(
                    `${process.env.REACT_APP_API_URL}/deleteartistbooking/${booking.id}`,
                    {
                        method: "DELETE",

                        headers: {
                            "Content-Type":
                                "application/json",

                            "Authorization":
                                "Bearer " + token
                        }
                    }
                );

            const data =
                await response.json();

            if (!response.ok) {
                throw new Error(
                    data?.message ||
                    "Failed to delete booking."
                );
            }

            /*
             * Refresh calendar
             */
            if (onSaved) {
                await onSaved(data);
            }

            /*
             * Close modal
             */
            onClose();

        } catch (error) {
            console.error(
                "Failed to delete booking:",
                error
            );

            setError(
                error.message ||
                "Failed to delete booking."
            );

        } finally {
            setDeleting(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    const isBusy =
        saving || deleting;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{
                backgroundColor:
                    "rgba(0, 0, 0, 0.5)"
            }}
        >
            <div
                className="card"
                style={{
                    width: "100%",
                    maxWidth: "600px",
                    maxHeight: "90vh",
                    overflowY: "auto",
                    margin: "20px"
                }}
            >

                {/* Header */}
                <header className="card-header">

                    <p className="card-header-title">

                        <span className="icon">
                            <i
                                className={
                                    isEditMode
                                        ? "mdi mdi-calendar-edit"
                                        : "mdi mdi-calendar-plus"
                                }
                            ></i>
                        </span>

                        {isEditMode
                            ? "Edit Artist Booking"
                            : "Add Artist Booking"}

                    </p>

                    <button
                        type="button"
                        className="card-header-icon"
                        onClick={onClose}
                        disabled={isBusy}
                        aria-label="Close"
                    >
                        <span className="icon">
                            <i className="mdi mdi-close"></i>
                        </span>
                    </button>

                </header>

                {/* Form */}
                <form onSubmit={handleSubmit}>

                    <div className="card-content">

                        {/* Error */}
                        {error && (
                            <div
                                style={{
                                    color: "#dc2626",
                                    background:
                                        "#fee2e2",
                                    padding: "10px",
                                    borderRadius: "4px",
                                    marginBottom: "15px"
                                }}
                            >
                                {error}
                            </div>
                        )}

                        {/* Title */}
                        <div className="field">

                            <label className="label">
                                Title
                            </label>

                            <div className="control">

                                <input
                                    type="text"
                                    className="input"
                                    value={title}
                                    onChange={(event) =>
                                        setTitle(
                                            event.target.value
                                        )
                                    }
                                    placeholder="Enter booking title"
                                    disabled={isBusy}
                                    required
                                />

                            </div>
                        </div>

                        {/* Description */}
                        <div className="field">

                            <label className="label">
                                Description
                            </label>

                            <div className="control">

                                <textarea
                                    className="textarea"
                                    value={description}
                                    onChange={(event) =>
                                        setDescription(
                                            event.target.value
                                        )
                                    }
                                    placeholder="Enter booking description"
                                    rows="4"
                                    disabled={isBusy}
                                />

                            </div>
                        </div>

                        {/* Start Date */}
                        <div className="field">

                            <label className="label">
                                Start Date & Time
                            </label>

                            <div className="control">

                                <input
                                    type="datetime-local"
                                    className="input"
                                    value={startDate}
                                    onChange={(event) =>
                                        setStartDate(
                                            event.target.value
                                        )
                                    }
                                    disabled={isBusy}
                                    required
                                />

                            </div>
                        </div>

                        {/* End Date */}
                        <div className="field">

                            <label className="label">
                                End Date & Time
                            </label>

                            <div className="control">

                                <input
                                    type="datetime-local"
                                    className="input"
                                    value={endDate}
                                    onChange={(event) =>
                                        setEndDate(
                                            event.target.value
                                        )
                                    }
                                    disabled={isBusy}
                                    required
                                />

                            </div>
                        </div>

                        {/* Status */}
                        <div className="field">

                            <label className="label">
                                Status
                            </label>

                            <div className="control">

                                <select
                                    className="input"
                                    value={status}
                                    onChange={(event) =>
                                        setStatus(
                                            event.target.value
                                        )
                                    }
                                    disabled={isBusy}
                                >
                                    <option value="confirmed">
                                        Confirmed
                                    </option>

                                    <option value="pending">
                                        Pending
                                    </option>

                                    <option value="cancelled">
                                        Cancelled
                                    </option>
                                </select>

                            </div>
                        </div>

                    </div>

                    {/* Footer */}
                    <footer
                        className="card-footer"
                        style={{
                            display: "flex",
                            justifyContent:
                                "space-between",
                            alignItems: "center",
                            padding: "15px"
                        }}
                    >

                        {/* Left side */}
                        <div>

                            {isEditMode && (
                                <button
                                    type="button"
                                    className="button is-danger"
                                    onClick={
                                        handleDelete
                                    }
                                    disabled={isBusy}
                                >
                                    {deleting
                                        ? "Deleting..."
                                        : "Delete"}
                                </button>
                            )}

                        </div>

                        {/* Right side */}
                        <div
                            style={{
                                display: "flex",
                                gap: "10px"
                            }}
                        >

                            <button
                                type="button"
                                className="button"
                                onClick={onClose}
                                disabled={isBusy}
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                className="button green"
                                disabled={isBusy}
                            >
                                {saving
                                    ? (
                                        isEditMode
                                            ? "Updating..."
                                            : "Saving..."
                                    )
                                    : (
                                        isEditMode
                                            ? "Update Booking"
                                            : "Save Booking"
                                    )}
                            </button>

                        </div>

                    </footer>

                </form>

            </div>
        </div>
    );
}

export default ArtistBookingModal;